/* Partner Demo Magic-Link Exchange (Feb 2026).

   Mounted at /partner-demo/:token. Anyone clicking the magic link an
   admin sent them lands here. We POST the token to the backend, get a
   read-only impersonation JWT back, then redirect to the right portal
   with `?_impersonate=<jwt>` — AuthContext picks that up automatically.
*/
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, Shield } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

export default function PartnerDemoExchange() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "loading", error: null, org: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/partner-demo/exchange", { token });
        if (cancelled) return;
        const portal = data.portal_url || "/";
        const separator = portal.includes("?") ? "&" : "?";
        // Hard reload so AuthContext (which only reads _impersonate on
        // mount) picks up the new token cleanly.
        window.location.replace(`${portal}${separator}_impersonate=${encodeURIComponent(data.token)}`);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        const reason = (typeof detail === "object" && detail?.reason) || "demo_link_error";
        const message = (typeof detail === "string" ? detail : detail?.message) ||
          "This demo link is not valid.";
        setState({ phase: "error", error: { reason, message }, org: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.phase === "loading") {
    return (
      <div data-testid="partner-demo-exchange-loading" className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
          <div className="text-sm text-slate-600">Preparing your demo session…</div>
          <div className="text-[11px] text-slate-400">Read-only · scoped to this organization</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="partner-demo-exchange-error" className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-50 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Demo link unavailable</h1>
            <p className="text-sm text-slate-600 mt-1">{state.error?.message}</p>
            <div className="text-[11px] text-slate-400 mt-2 font-mono">code: {state.error?.reason}</div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex gap-2">
          <Shield className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div>
            Demo links are time-bound and use-capped. Please ask your
            verqo contact to send you a fresh link.
          </div>
        </div>
        <Button variant="outline" onClick={() => nav("/login")} data-testid="partner-demo-back-to-login">
          Back to sign-in
        </Button>
      </div>
    </div>
  );
}
